import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
	Smartphone,
	Zap,
	CreditCard,
	Bell,
	Star,
	Globe,
	Shield,
	TrendingUp,
} from "lucide-react";

export default function Benefits() {
	const { t } = useTranslation();

	const benefits = [
		{
			title: t("benefits.items.app.title"),
			description: t("benefits.items.app.description"),
			icon: <Smartphone className="w-7 h-7 text-primary" />,
			highlight: t("benefits.items.app.highlight"),
		},
		{
			title: t("benefits.items.web.title"),
			description: t("benefits.items.web.description"),
			icon: <Globe className="w-7 h-7 text-primary" />,
			highlight: t("benefits.items.web.highlight"),
		},
		{
			title: t("benefits.items.bookings.title"),
			description: t("benefits.items.bookings.description"),
			icon: <Zap className="w-7 h-7 text-primary" />,
			highlight: t("benefits.items.bookings.highlight"),
		},
		{
			title: t("benefits.items.orders.title"),
			description: t("benefits.items.orders.description"),
			icon: <CreditCard className="w-7 h-7 text-primary" />,
			highlight: t("benefits.items.orders.highlight"),
		},
		{
			title: t("benefits.items.loyalty.title"),
			description: t("benefits.items.loyalty.description"),
			icon: <Star className="w-7 h-7 text-primary" />,
			highlight: t("benefits.items.loyalty.highlight"),
		},
		{
			title: t("benefits.items.notifications.title"),
			description: t("benefits.items.notifications.description"),
			icon: <Bell className="w-7 h-7 text-primary" />,
			highlight: t("benefits.items.notifications.highlight"),
		},
	];

	return (
		<section id="funcionalidades" className="py-20 md:py-28 bg-white">
			<div className="max-w-6xl mx-auto px-6">
				{/* Header */}
				<div className="text-center mb-16">
					<motion.span
						className="inline-block text-primary text-sm font-semibold uppercase tracking-wider mb-4"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5 }}
						viewport={{ once: true }}
					>
						{t("benefits.badge")}
					</motion.span>
					<motion.h2
						className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.1 }}
						viewport={{ once: true }}
					>
						{t("benefits.title")}
					</motion.h2>
					<motion.p
						className="text-lg text-gray-600 max-w-2xl mx-auto"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						viewport={{ once: true }}
					>
						{t("benefits.subtitle")}
					</motion.p>
				</div>

				{/* Benefits Grid */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
					{benefits.map((b, i) => (
						<motion.div
							key={b.title}
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ delay: i * 0.1, duration: 0.5, ease: "easeOut" }}
							viewport={{ once: true, amount: 0.2 }}
							className="group"
						>
							<div className="relative bg-white rounded-2xl p-6 md:p-8 border border-gray-100 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 h-full flex flex-col">
								{/* Highlight badge */}
								{b.highlight && (
									<span style={{ backgroundColor: "#ECF9F5" }} className="absolute -top-3 right-6 text-primary text-xs font-semibold px-3 py-1 rounded-full">
										{b.highlight}
									</span>
								)}

								{/* Icon */}
								<div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 mb-5 group-hover:scale-110 transition-transform duration-300">
									{b.icon}
								</div>

								{/* Content */}
								<h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
									{b.title}
								</h3>
								<p className="text-gray-600 text-sm md:text-base leading-relaxed flex-grow">
									{b.description}
								</p>
							</div>
						</motion.div>
					))}
				</div>

				{/* Bottom highlight */}
				<motion.div
					className="mt-12 text-center"
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.4 }}
					viewport={{ once: true }}
				>
					<div className="inline-flex items-center gap-3 bg-gradient-to-r from-primary/5 to-primary/10 rounded-full px-6 py-3">
						<Shield className="w-5 h-5 text-primary" />
						<span className="text-sm md:text-base font-medium text-gray-700">
							{t("benefits.bottomNote")}
						</span>
						<TrendingUp className="w-5 h-5 text-primary" />
					</div>
				</motion.div>
			</div>
		</section>
	);
}
